/**
 * IsaacNoYoutube - the third game on the GAME-LEVEL-ENGINE.
 *
 * A screen is glowing in the corner and the room is full of people. Pip says a
 * name; Isaac says it back; that person comes over and the room gets a little
 * warmer. Across five levels the room fills and the screen goes out on its own.
 *
 * =============================================================================
 * WHAT IS LEFT IN A GAME
 *
 * Content (`content/`), a mechanic (this file), and its own judgements
 * (`logic/`). Everything else is the platform's:
 *
 *   shared/trialLoop.js     the trial, and the only implementation of it
 *   shared/browserStage.js  the device: a synthesiser, a recogniser, timers
 *   shared/step.js          how many goes a step gets, and where each opens
 *   shared/pacing.js        obeying the engine's recommendation
 *   engine/*                the framework, the judgements, the scoring, the rules
 *
 * This file decides what the child SEES and what happens when they speak. It
 * decides no pacing, colours no box, numbers no attempt and fades no prompt.
 *
 * =============================================================================
 * THE SCREEN IN THE CORNER
 *
 * `glow` is drawn and is never a target. It is not in the vocabulary, not in
 * `legalMoves`, not in an accept list, and nothing about it is scored or sent.
 * Its light falls as the level's people arrive and it never comes back up -
 * within a level it only ever dims, and a level that ends with the child having
 * struggled still ends with it dimmer than it started.
 *
 * That last part is deliberate and it is a rule, not a detail. A glow that rose
 * again when the child missed would be a fail state with the buzzer taken out:
 * the child would learn that the screen wins when they cannot find the word,
 * which is the exact association this game exists to break. Section 2.4 - there
 * is no fail state - is a rule about what a child can SEE, not only about what
 * is scored.
 *
 * =============================================================================
 * AUDIO ENERGY IS NOT OPTIONAL HERE
 *
 * Five of this game's fifteen boxes are Attempts Vocalization, which the engine
 * judges from the audio envelope. `useAudioEnergy` is started on the same user
 * gesture as the recogniser. If the microphone is refused the game still plays
 * and every trial records `monitored: false`, which the engine reads as "not
 * measured" rather than "made no sound" - a child who was never listened to
 * must not be recorded as silent.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './IsaacNoYoutube.css';

import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../../../hooks/useSpeechSynthesis';
import { useAudioEffects } from '../../../hooks/useAudioEffects';
import { useAudioEnergy } from '../../../hooks/useAudioEnergy';

import { evaluateProgression } from '../../../engine/progression.js';
import { getLevel, getPromptStyle, getStrictness } from '../../../engine/framework.js';
import { levelBoxSummary } from '../../../engine/contract.js';
import { redact } from '../../../engine/language.js';

import { runTrial } from '../shared/trialLoop.js';
import { createBrowserStage } from '../shared/browserStage.js';
import { createStep } from '../shared/step.js';
import { readRecommendation } from '../shared/pacing.js';
import { openingRungIndex, rungIndexOf } from '../shared/support.js';
import { placeChildInGame } from '../shared/profile.js';
import { createBags } from '../shared/lineBag.js';
import { createRng } from '../shared/rounds.js';
import useAttemptRecorder from '../shared/useAttemptRecorder.js';
import PipCharacter from '../characters/PipCharacter';

// This game's own.
import { createAdapter } from './adapter.js';
import { GAME, POOLS } from './content/index.js';
import { getNearby } from './content/nearby.js';
import * as VOICE from './content/voice.js';
import { childrenApi } from '../../../utils/api';

/** How long somebody stays lit up before the next name. */
const ARRIVE_MS = 750;

/** The screen's light, as a level opens. It only ever falls from here. */
const GLOW_START = { bright: 1, fading: 0.55, out: 0.15 };

export default function IsaacNoYoutube({ onExit }) {
  const speech = useSpeechRecognition();
  const synth = useSpeechSynthesis();
  const fx = useAudioEffects(true);
  const energy = useAudioEnergy();

  const env = useRef({});
  env.current = { ...env.current, speech, synth, fx, energy, soundOn: true };

  const [screen, setScreen] = useState('start');
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState(null);
  const [position, setPosition] = useState(null);
  const [view, setView] = useState({ cards: [], prompt: '', cue: null, scene: 'sofa' });
  const [glow, setGlow] = useState(1);
  const [banner, setBanner] = useState(null);
  const [pip, setPip] = useState({ mood: 'happy', animation: 'idle' });
  const [heard, setHeard] = useState(null);

  // ---- the loop's own state, in refs. A setState that lands a frame late is a
  // nuisance in a UI and a corrupted trial record in a measurement instrument.
  const positionRef = useRef(null);
  const contentRef = useRef(null);
  const targetsRef = useRef([]);
  const supportIndexRef = useRef(0);
  const supportIncreasesRef = useRef(0);
  const attemptsRef = useRef([]);
  const levelScoreRef = useRef(null);
  const offerRef = useRef(null);
  const stepRef = useRef(null);
  const aliveRef = useRef(true);
  const bagsRef = useRef(null);
  const rngRef = useRef(null);
  const adapterRef = useRef(null);
  const stageRef = useRef(null);
  // How far the screen's light has fallen this level. A ref as well as state
  // because the trial loop reads it between awaits.
  const glowRef = useRef(1);

  const recorder = useAttemptRecorder({
    childId: child ? child.id : null,
    gameId: GAME.id,
    onRecommendation: (rec, result) => {
      // The chart's own verdict on this level (section 7.5), which the local
      // evaluator cannot compute - it holds no box histories.
      if (result && result.level) levelScoreRef.current = result.level;
      applyPacing(rec);
    },
  });
  env.current.recorder = recorder;

  useEffect(() => {
    childrenApi.list().then((d) => setChildren(d.children || [])).catch(() => setChildren([]));
  }, []);

  const line = useCallback((pool, prefer) => {
    const bag = bagsRef.current && bagsRef.current[pool];
    return bag ? bag.next(prefer) : '';
  }, []);

  /** The screen only ever dims. See the header. */
  const dimScreen = useCallback((by) => {
    glowRef.current = Math.max(0, glowRef.current - by);
    setGlow(glowRef.current);
  }, []);

  // ==========================================================================
  // THE STAGE - everything the trial loop needs from a real device
  // ==========================================================================
  if (!stageRef.current) {
    stageRef.current = createBrowserStage({
      env,
      post: (payload) => env.current.recorder.record(payload),
      show: (patch) => {
        if (patch.kind === 'prompt') {
          setView({
            scene: contentRef.current ? contentRef.current.scene : 'sofa',
            cards: (patch.target.onScreen || [patch.target.objectId]).map((id) => ({
              id, here: false, asked: id === patch.target.objectId,
            })),
            prompt: patch.prompt.text,
            cue: patch.prompt.cue ? patch.prompt.cue.text : null,
          });
          setPip((p) => ({ ...p, mood: 'curious' }));
          setHeard(null);
          return;
        }
        if (patch.kind === 'ladder') {
          setView((v) => ({
            ...v,
            prompt: patch.prompt.text,
            cue: patch.prompt.cue ? patch.prompt.cue.text : null,
          }));
          return;
        }
        if (patch.kind === 'hint') {
          setView((v) => ({ ...v, prompt: patch.text }));
          return;
        }
        if (patch.kind === 'listening') {
          setPip((p) => ({ ...p, mood: 'happy' }));
          setBanner(null);
        }
      },
    });
  }

  const say = useCallback((text) => stageRef.current.say(text), []);
  const pause = useCallback(
    (ms) => new Promise((resolve) => { stageRef.current.later(ms, resolve); }),
    []
  );

  // ==========================================================================
  // PACING - the engine's, never this game's
  //
  // At Phase 1 both support actions are no-ops: the phase's bounds are 0..0 and
  // applyRecommendation() clamps into them, so `changed` comes back false. The
  // code is here unchanged all the same, because a game that special-cased its
  // own phase would be the game that broke when somebody gave it a Phase 2
  // level.
  // ==========================================================================
  function applyPacing(rec) {
    const phase = positionRef.current ? positionRef.current.phase : 1;
    const read = readRecommendation(rec, { phase, supportIndex: supportIndexRef.current });

    if (read.support && read.support.changed) {
      supportIndexRef.current = read.support.index;
      if (read.support.raise) {
        // Support before failure (section 2.4): a raise takes effect on the
        // very next trial, including a retry of the step in progress.
        supportIncreasesRef.current += 1;
        if (stepRef.current) stepRef.current.supportRaised(read.support.index);
      }
    }
    // An offer is an offer: held until the level ends and presented there.
    if (read.offer) offerRef.current = read.offer;
  }

  // ==========================================================================
  // ONE STEP - one person the child is asked for, and its trials
  // ==========================================================================
  const playStep = useCallback(async (index) => {
    const { phase, level } = positionRef.current;
    const content = contentRef.current;
    const target = targetsRef.current[index];
    const step = createStep({ phase, openingIndex: supportIndexRef.current });
    stepRef.current = step;
    // Spread across the level's steps, so the last one lands near zero however
    // many steps a level has.
    const dimPerStep = GLOW_START[content.glow] / Math.max(1, targetsRef.current.length);

    for (;;) {
      const result = await runTrial({
        stage: stageRef.current,
        game: adapterRef.current,
        phase,
        level,
        content,
        target,
        pacing: adapterRef.current.pacing(phase, level),
        openingIndex: step.open(),
        attemptNumber: step.attemptNumber(),
        step: index + 1,
        supportIncreases: supportIncreasesRef.current,
      });
      if (result.cancelled || !aliveRef.current) return false;
      if (!result.ok) {
        // The game built something the engine would refuse. Nothing is posted -
        // a bad record is worse than no record - and the child plays on.
        // eslint-disable-next-line no-console
        console.error('[isaac-no-youtube] the attempt was not posted:',
          result.validated.errors, result.check.errors);
      }

      // Never straight from the recogniser: `raw.transcript` is what the
      // microphone returned and a real session has stored an obscenity there
      // for a child who said "fox". Everything on screen goes through redact().
      setHeard(redact(result.raw.transcript));

      // Whole-attempt outcomes at THIS level, which is what section 8.4 reads.
      attemptsRef.current = [...attemptsRef.current, result.outcome];
      applyPacing(evaluateProgression({
        phase,
        level,
        attempts: attemptsRef.current,
        supportIndex: supportIndexRef.current,
        supportIncreases: supportIncreasesRef.current,
        targetSupportIndex: rungIndexOf(content.nominalRung),
        levelScore: levelScoreRef.current,
        availablePhases: GAME.phasesAvailable,
        availableLevels: GAME.levelsAvailable,
      }));

      if (result.judged.success) {
        setView((v) => ({
          ...v,
          cards: v.cards.map((c) => (c.id === target.objectId ? { ...c, here: true } : c)),
        }));
        dimScreen(dimPerStep);
        env.current.fx.playSuccess?.();
        setPip({ mood: 'excited', animation: 'celebrate' });
        const praise = line('praise', 'bright');
        setBanner({ kind: 'praise', text: praise });
        await say(praise);
        await pause(ARRIVE_MS);
        setBanner(null);
        setPip({ mood: 'happy', animation: 'idle' });
        return true;
      }

      // Never a failure. Three different things can have just happened and they
      // deserve three different answers; only the last is anything like an error.
      // A trial with no transcript at all is the one where the child may still
      // have vocalised - Phase 1 component B - so it gets acknowledgement, not
      // correction.
      const text = line(result.raw.transcript ? 'nearly' : 'encouragement', 'calm');
      setBanner({ kind: 'try', text });
      await say(text);
      if (!aliveRef.current) return false;

      if (!step.missed(result)) {
        // Pip does it with them. NOT recorded as an attempt: nothing was
        // measured, and writing a failure here would punish the child for the
        // game running out of patience. The person still arrives, and the
        // screen still dims - see the header.
        await say(line('assist'));
        await say(adapterRef.current.phraseFor(target));
        setView((v) => ({
          ...v,
          cards: v.cards.map((c) => (c.id === target.objectId ? { ...c, here: true } : c)),
        }));
        dimScreen(dimPerStep);
        await pause(500);
        return true;
      }
      await pause(400);
      if (!aliveRef.current) return false;
    }
  }, [dimScreen, line, pause, say]);

  // ==========================================================================
  // ONE LEVEL
  // ==========================================================================
  const playLevel = useCallback(async (phase, level, { intro = false } = {}) => {
    const content = adapterRef.current.content(phase, level);
    if (!content) return null;

    const previous = positionRef.current;
    const sameLevel = Boolean(previous && previous.phase === phase && previous.level === level);

    positionRef.current = { phase, level };
    contentRef.current = content;
    targetsRef.current = adapterRef.current.plan(phase, level).flatMap((r) => r.targets);

    // A level is REPLAYED until its boxes have ten plays each, so its history
    // belongs to the LEVEL and not to one pass through it. Clearing it every
    // pass makes section 8.4's five-attempt window unreachable on any level
    // shorter than five trials, which is most of them.
    if (!sameLevel) {
      attemptsRef.current = [];
      supportIncreasesRef.current = 0;
      offerRef.current = null;
      levelScoreRef.current = null;
    }

    // The screen's light is a property of the LEVEL, so it resets at the top of
    // each new level and not at each pass through one - a child replaying a
    // level to finish its ten plays should not watch the room darken again.
    if (!sameLevel) {
      glowRef.current = GLOW_START[content.glow];
      setGlow(glowRef.current);
    }

    supportIndexRef.current = openingRungIndex({
      phase,
      levelNominalRung: content.nominalRung,
      placementIndex: supportIndexRef.current,
    });
    setPosition({ phase, level });
    setView((v) => ({ ...v, scene: content.scene }));

    if (intro) {
      for (const text of content.opening) {
        // eslint-disable-next-line no-await-in-loop
        await say(text);
        if (!aliveRef.current) return null;
      }
    }

    for (let i = 0; i < targetsRef.current.length; i += 1) {
      if (i > 0) {
        // eslint-disable-next-line no-await-in-loop
        await say(line('roundLink'));
        if (!aliveRef.current) return null;
      }
      // eslint-disable-next-line no-await-in-loop
      const carriedOn = await playStep(i);
      if (!carriedOn) return null;
    }

    await say(line('levelComplete'));
    if (!aliveRef.current) return null;

    const offer = offerRef.current;
    if (offer && offer.kind === 'complete') return null;
    // Nobody has to tap. With an offer standing, take it; otherwise play the
    // level again - ten plays is what a box needs before it can be coloured.
    return offer && offer.target ? offer.target : { phase, level };
  }, [line, playStep, say]);

  // ==========================================================================
  // A SESSION
  // ==========================================================================
  const begin = useCallback(async (chosen) => {
    setChild(chosen);
    rngRef.current = createRng(Math.floor(Math.random() * 2 ** 31) || 1);
    adapterRef.current = createAdapter({ rng: rngRef.current });
    bagsRef.current = createBags({
      ...POOLS,
      roundLink: VOICE.ROUND_LINKS,
      levelComplete: VOICE.LEVEL_COMPLETE,
      sessionEnd: VOICE.SESSION_END,
      intro1: VOICE.LEVEL_INTRO[1],
    }, { rng: rngRef.current });

    // Section 9.2: ask the engine where to start, from the CHILD's phase and
    // level. Never decide it here, and never re-clamp the answer: the chart
    // tracks the child, not the game (section 9.1).
    const placement = placeChildInGame(chosen, GAME);
    supportIndexRef.current = placement.supportIndex;

    // Both microphone paths, on one user gesture. The energy one is what makes
    // component B measurable at all.
    await env.current.energy.start();
    env.current.speech.startListening();
    setScreen('play');

    await say(line('intro1'));

    let next = { phase: placement.phase, level: placement.level };
    let intro = true;
    while (next && aliveRef.current) {
      const from = next;
      // eslint-disable-next-line no-await-in-loop
      next = await playLevel(from.phase, from.level, { intro });
      intro = Boolean(next) && (next.phase !== from.phase || next.level !== from.level);
    }
    if (aliveRef.current) {
      env.current.speech.stopListening?.();
      env.current.energy.stop?.();
      env.current.recorder.flush();
      setScreen('end');
      await say(line('sessionEnd'));
    }
  }, [line, playLevel, say]);

  // The recogniser's callbacks go to the stage, which queues them for the loop.
  useEffect(() => {
    aliveRef.current = true;
    const detach = stageRef.current.attach();
    return () => {
      aliveRef.current = false;
      detach();
      env.current.synth.cancel?.();
      env.current.speech.stopListening?.();
      env.current.energy.stop?.();
    };
  }, []);

  /** The grown-ups line: what the engine says this level is, not what we think. */
  const engineFacts = useMemo(() => {
    if (!position) return null;
    const { phase, level } = position;
    return {
      level: getLevel(phase, level),
      prompt: getPromptStyle(phase),
      strictness: getStrictness(phase),
      boxes: levelBoxSummary(phase, level, GAME.capabilities),
    };
  }, [position]);

  if (!speech.isSupported) {
    return (
      <div className="iny iny--message">
        <div className="iny__card">
          <h2>This game needs a microphone</h2>
          <p>It is played with your voice. Chrome, Edge or Safari will work.</p>
          <button type="button" className="iny__btn" onClick={onExit}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`iny iny--${view.scene}`}>
      {screen === 'start' && (
        <div className="iny__card iny__start">
          <h1>Isaac No Youtube</h1>
          <p>Say who is here, and they come over.</p>
          {children.map((c) => (
            <button key={c.id} type="button" className="iny__btn iny__btn--child" onClick={() => begin(c)}>
              {c.name} · phase {c.currentPhase || 1}, level {c.currentLevel || 1}
            </button>
          ))}
          <button type="button" className="iny__btn" onClick={() => begin(null)}>
            Play without recording
          </button>
          <button type="button" className="iny__btn iny__btn--quiet" onClick={onExit}>Back to games</button>
        </div>
      )}

      {screen === 'play' && (
        <div className="iny__play">
          {/* Scenery. Never a target, never named, never scored. */}
          <div
            className="iny__glow"
            style={{ opacity: glow }}
            aria-hidden="true"
          >
            <span className="iny__glow-box" />
          </div>

          <div className={`iny__room iny__room--${view.cards.length}`}>
            {view.cards.map((card) => {
              const one = getNearby(card.id);
              return (
                <div
                  key={card.id}
                  className={`iny-card${card.here ? ' is-here' : ''}${card.asked ? ' is-asked' : ''}`}
                  style={{ '--tint': one.tint }}
                >
                  <span className="iny-card__face" role="img" aria-label={one.word}>{one.art}</span>
                </div>
              );
            })}
            {banner && <div className={`iny__banner iny__banner--${banner.kind}`}>{banner.text}</div>}
          </div>

          <div className="iny__pip">
            <PipCharacter mood={pip.mood} animation={pip.animation} size="medium" />
          </div>

          <div className="iny__prompt">
            <p className="iny__line">{view.prompt}</p>
            {view.cue && <span className="iny__cue">{view.cue}</span>}
            {heard && <p className="iny__heard">Pip heard: {heard}</p>}
            {engineFacts && (
              <p className="iny__where">
                {engineFacts.level.phaseName} · {engineFacts.prompt.style}
                {' · '}{engineFacts.strictness.id}
                {' · '}level {position.level} of {GAME.totalLevels}
              </p>
            )}
          </div>
        </div>
      )}

      {screen === 'end' && (
        <div className="iny__card">
          <h2>Everybody came</h2>
          <p>Isaac has all the people he needs.</p>
          <button type="button" className="iny__btn" onClick={onExit}>Back to games</button>
        </div>
      )}
    </div>
  );
}
